<template>
  <div
    v-if="visible"
    class="epo-product-banner"
    :class="'epo-product-banner--' + phaseClass"
    role="status"
  >
    <img
      class="epo-product-banner__icon"
      :src="iconSrc"
      width="40"
      height="40"
      alt=""
    />
    <div class="epo-product-banner__message">
      <div class="epo-product-banner__body">
        <div class="epo-product-banner__title">{{ titleText }}</div>
        <div class="epo-product-banner__text">{{ bodyText }}</div>
      </div>
      <button
        type="button"
        class="epo-product-banner__cta"
        :t_label="'EMPBE_EPO_ProductBanner_ContactSales' + epoTrackSuffix"
        @click="openContactSales"
      >
        {{ $t('lbl_EMPBE_EPO_Banner_ContactSales') }}
      </button>
    </div>
    <AdListContactUs ref="epoContactUs" submit-page-url="EpoProductBannerContactUs" />
  </div>
</template>

<script>
import { epoService } from '../_services/epo.service';
import { normalizeApiResponse } from '../_helpers/apiResponse';
import AdListContactUs from '../pages/advertisingInfoCenter/AdListContactUs.vue';

export default {
  name: 'EpoProductBanner',
  components: { AdListContactUs },
  data() {
    return {
      loading: false,
      phase: 'None',
      notice: {
        needNotice: false,
        scheduledDate: '',
        actionDeadline: '',
        excessProductCount: 0,
        clearedProductCount: 0,
        daysRemaining: 0,
        jobClearDays: 7,
        targetProductQty: 0,
        currentProductQty: 0,
        currentProductUsed: 0,
      },
    };
  },
  computed: {
    epoTrackSuffix() {
      const u = this.$store?.state?.authentication?.user || {};
      const companyId = u.empCompanyId || u.EmpCompanyId || 0;
      const roleUserId = u.accountUserId || u.AccountUserId || 0;
      return `_${companyId}_${roleUserId}`;
    },
    /** Reminder/Action：仍超额；Notify：仍超额或 Job 已下架（与角色蓝条一致） */
    visible() {
      if (!this.notice.needNotice) return false;
      if (this.phase === 'Reminder' || this.phase === 'Action') {
        return this.notice.excessProductCount > 0;
      }
      if (this.phase === 'Notify') {
        return this.notice.excessProductCount > 0 || this.notice.clearedProductCount > 0;
      }
      return false;
    },
    phaseClass() {
      if (this.phase === 'Action') return 'action';
      if (this.phase === 'Notify') return 'notify';
      return 'reminder';
    },
    iconSrc() {
      const base = process.env.BASE_URL || '/';
      if (this.phase === 'Action') return `${base}img/epo-plan-changed-action.svg`;
      if (this.phase === 'Notify') return `${base}img/epo-plan-changed-notify.svg`;
      return `${base}img/epo-plan-changed-remind.svg`;
    },
    dateText() {
      return this.formatDate(this.notice.scheduledDate);
    },
    deadlineText() {
      if (this.notice.actionDeadline) return this.formatDate(this.notice.actionDeadline);
      const raw = this.notice.scheduledDate;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      d.setDate(d.getDate() + 6);
      return this.formatDate(d);
    },
    autoDateText() {
      const raw = this.notice.scheduledDate;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      d.setDate(d.getDate() + (this.notice.jobClearDays || 7));
      return this.formatDate(d);
    },
    copyParams() {
      return {
        date: this.dateText,
        deadline: this.deadlineText,
        autoDate: this.autoDateText,
        old: this.notice.currentProductQty,
        new: this.notice.targetProductQty,
        count: this.notice.currentProductUsed,
        excess: this.notice.excessProductCount,
      };
    },
    titleText() {
      if (this.phase === 'Action') {
        return this.$t('lbl_EMPBE_EPO_ProductBanner_ActionTitle', this.copyParams);
      }
      if (this.phase === 'Notify') {
        return this.$t('lbl_EMPBE_EPO_ProductBanner_NotifyStillOverTitle', this.copyParams);
      }
      return this.$t('lbl_EMPBE_EPO_ProductBanner_ReminderTitle', this.copyParams);
    },
    bodyText() {
      if (this.phase === 'Action') {
        return this.$t('lbl_EMPBE_EPO_ProductBanner_ActionBody', this.copyParams);
      }
      if (this.phase === 'Notify') {
        return this.$t('lbl_EMPBE_EPO_ProductBanner_NotifyStillOverBody', this.copyParams);
      }
      return this.$t('lbl_EMPBE_EPO_ProductBanner_ReminderBody', this.copyParams);
    },
  },
  mounted() {
    this.loadNotice();
  },
  methods: {
    toQty(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    formatDate(raw) {
      if (!raw) return '';
      const d = raw instanceof Date ? raw : new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    },
    canShow() {
      const user = this.$store?.state?.authentication?.user;
      if (!user) return false;
      if (String(user.userType || '').toUpperCase() === 'ADMIN') return false;
      return true;
    },
    /** 列表删除/下架成功后由父组件调用，刷新「需下架/删除 x 个」文案 */
    async loadNotice() {
      if (!this.canShow()) return;
      this.loading = true;
      try {
        const rawRes = await epoService.GetRoleInactiveNotice();
        const res = normalizeApiResponse(rawRes);
        if (!res.isSuccessful) return;
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        if (!row) {
          this.phase = 'None';
          this.notice = {
            ...this.notice,
            needNotice: false,
            excessProductCount: 0,
            clearedProductCount: 0,
            currentProductUsed: 0,
          };
          return;
        }
        this.phase = String(row.phase ?? row.Phase ?? 'None');
        this.notice = {
          needNotice: !!(row.needNotice ?? row.NeedNotice),
          scheduledDate: row.scheduledDate ?? row.ScheduledDate ?? '',
          actionDeadline: row.actionDeadline ?? row.ActionDeadline ?? '',
          excessProductCount: this.toQty(row.excessProductCount ?? row.ExcessProductCount),
          clearedProductCount: this.toQty(row.clearedProductCount ?? row.ClearedProductCount),
          daysRemaining: this.toQty(row.daysRemaining ?? row.DaysRemaining),
          jobClearDays: this.toQty(row.jobClearDays ?? row.JobClearDays) || 7,
          targetProductQty: this.toQty(row.targetProductQty ?? row.TargetProductQty),
          currentProductQty: this.toQty(row.currentProductQty ?? row.CurrentProductQty),
          currentProductUsed: this.toQty(row.currentProductUsed ?? row.CurrentProductUsed),
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[EPO ProductBanner] load failed', e);
      } finally {
        this.loading = false;
      }
    },
    openContactSales() {
      const cu = this.$refs.epoContactUs;
      if (cu && typeof cu.open === 'function') cu.open();
    },
  },
};
</script>

<style lang="scss" scoped>
.epo-product-banner {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  gap: 8px;
  align-items: flex-start;

  &--reminder {
    background: #fffce5;
    border-color: #e98024;
  }

  &--action {
    background: #fcebed;
    border-color: #cd1515;
  }

  &--notify {
    background: #e7f8ff;
    border-color: #2c6ecb;
  }
}

.epo-product-banner__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: block;
}

.epo-product-banner__message {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.epo-product-banner__body {
  flex: 1 1 268px;
  max-width: 70%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.epo-product-banner__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

.epo-product-banner__text {
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

.epo-product-banner__cta {
  flex-shrink: 0;
  margin: 0;
  padding: 8px 12px;
  border: 1px solid #7968ff;
  border-radius: 4px;
  background: transparent;
  color: #7366ff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;

  &:hover {
    background: #4433ff;
    border-color: #4433ff;
    color: #fff;
  }
}
</style>
